import { RefObject, useCallback, useRef, useState } from 'react';

import { useOnEscapePress } from '@/hooks/useOnEscapePress';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';

interface UseDropdown<ContainerElement> {
  isOpen: boolean;
  close: () => void;
  open: () => void;
  handleOpenClose: (value: boolean) => void;
  containerRef: RefObject<ContainerElement>;
}

export const useDropdown = <ContainerElement extends HTMLElement>(config?: {
  defaultOpen?: boolean;
  /**
   * Whether focus should be returned to the trigger on close. Defaults to true.
   * Disable it when the trigger is itself the focusable element that opens the
   * dropdown (e.g. a search input opened via `onFocus`), where restoring focus
   * would immediately re-open it.
   */
  restoreFocusOnClose?: boolean;
}): UseDropdown<ContainerElement> => {
  const { defaultOpen, restoreFocusOnClose = true } = config ?? {};
  const [isOpen, setIsOpen] = useState(!!defaultOpen);
  const ref = useRef<ContainerElement>(null);
  // The element that had focus when the dropdown was opened. Focus is returned
  // here on close so keyboard users are not dropped to the page <body>.
  const triggerRef = useRef<HTMLElement | null>(null);

  const captureTrigger = useCallback(() => {
    triggerRef.current = document.activeElement as HTMLElement | null;
  }, []);

  const restoreFocus = useCallback(() => {
    if (!restoreFocusOnClose) return;

    const trigger = triggerRef.current;

    if (!trigger || typeof trigger.focus !== 'function') return;
    // Only restore focus when it currently lives inside the dropdown (so it
    // would otherwise be lost), e.g. closing via Escape or selecting an item.
    // Closing by clicking elsewhere should not yank focus back to the trigger.
    const active = document.activeElement;
    const focusIsInside = active === document.body || (ref.current?.contains(active as Node) ?? false);

    if (focusIsInside) {
      trigger.focus();
    }
  }, [restoreFocusOnClose]);

  const close = useCallback(() => {
    setIsOpen(false);
    restoreFocus();
  }, [restoreFocus]);

  const open = useCallback(() => {
    captureTrigger();
    setIsOpen(true);
  }, [captureTrigger]);

  const handleOpenClose = useCallback(
    (value: boolean) => {
      if (value) {
        captureTrigger();
        setIsOpen(true);
      } else {
        close();
      }
    },
    [captureTrigger, close],
  );

  useOnClickOutside(isOpen, close, ref);
  useOnEscapePress(close);

  return {
    isOpen,
    close,
    open,
    handleOpenClose,
    containerRef: ref,
  };
};
